/**
 * sdiSpec — `sdi_invoicing` capability (white-label SDI).
 *
 * Per CLAUDE.md `feedback_white_label_capability_naming.md`: ``sdi_invoicing``
 * IS in the white-label-mandatory list. Vendor name (Acube / Aruba SDI)
 * MUST NEVER appear in the FE picker, labels, or error copy. PEC is the
 * sole exception to that rule; SDI is NOT.
 *
 * Routing dispatch (Acube vs Aruba SDI vs future drivers) happens BE-side
 * via ``ConnectorFactory._resolve(tenant_id, "sdi_invoicing")``. The
 * tenant_backend_overrides table (planned) will let admins flip the
 * backend per tenant — invisible to the user.
 *
 * "Use my existing Aruba SDI account" UX is a Phase-2 BYOA flow modelled
 * on ``crmSpec``: an OPTIONAL credentials step gated by a vendor-neutral
 * toggle ("Account dedicato (opzionale)"). Once shipped, this stays the
 * single white-label entry and the BE schema dispatch returns the
 * Aruba-shaped form when those creds are present.
 *
 * v1 source-of-truth: none. Roadmap pivot May 10, 2026 (Italian
 * Connectors / Path C).
 */

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { FileCheck2, Receipt, Shield, Zap } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";

export interface SdiConnection extends BaseConnection {
  readonly company_id?: string;
  readonly company_name?: string;
  readonly vat_number?: string;
  readonly sdi_destination_code?: string;
}

const iconCls = "h-4 w-4";

export const sdiSpec: CapabilityWizardSpec<SdiConnection> = {
  capability: "sdi_invoicing",
  title: msg`Fatturazione SDI`,
  subtitle: msg`Trasmissione fatture al Sistema di Interscambio`,
  icon: <FileCheck2 className="h-5 w-5" aria-hidden />,
  // 30min: aligned with accounting/PEC/cassetto baseline. OB diverges
  // to 60min because PSD2 retention semantics are different.
  gdprDeleteWindowMinutes: 30,

  // Single white-label vendor entry per CLAUDE.md
  // ``feedback_white_label_capability_naming.md``. Backend dispatch
  // (Acube vs Aruba SDI vs future) is invisible to the user — picked
  // by ``ConnectorFactory._resolve`` via tenant overrides.
  vendors: [
    {
      id: "sdi_invoicing",
      label: msg`Sistema di Interscambio`,
      icon: <FileCheck2 className={iconCls} aria-hidden />,
      whiteLabel: true,
    },
  ],

  steps: [
    {
      id: "company",
      kind: "form",
      title: msg`Azienda`,
      description: msg`Dati anagrafici necessari per la trasmissione al SDI.`,
      fields: [
        { name: "company_name", label: msg`Ragione sociale`, type: "string", required: true },
        {
          name: "vat_number",
          label: msg`Partita IVA`,
          type: "string",
          required: true,
          placeholder: "IT01234567890",
          helperText: msg`11 cifre con prefisso IT (es. IT01234567890).`,
        },
        {
          name: "sdi_destination_code",
          label: msg`Codice destinatario (7 char)`,
          type: "string",
          placeholder: "0000000",
          helperText: msg`Se non hai un codice SDI dedicato lascia 0000000: il SDI userà la PEC che hai collegato come canale di consegna.`,
        },
      ],
    },
    {
      id: "confirm",
      kind: "confirm",
      title: msg`Conferma`,
      description: msg`Verifica i dati. Ando trasmetterà le fatture al SDI solo dopo la tua conferma su ogni proposta (HITL).`,
    },
  ],

  benefits: [
    {
      icon: <Receipt className={iconCls} aria-hidden />,
      title: msg`Invio automatico`,
      body: msg`Genera + trasmetti + ricevuta SDI senza usare il portale AdE.`,
    },
    {
      icon: <Zap className={iconCls} aria-hidden />,
      title: msg`Conservazione integrata`,
      body: msg`Le ricevute restano nel Cassetto Fiscale (capability separata).`,
    },
    {
      icon: <FileCheck2 className={iconCls} aria-hidden />,
      title: msg`Stato chiaro`,
      body: msg`Ando notifica scarti, accettazioni, mancate consegne.`,
    },
    {
      icon: <Shield className={iconCls} aria-hidden />,
      title: msg`Solo in lettura senza conferma`,
      body: msg`Bozze proposte, mai inviate in autonomia (HITL).`,
    },
  ],

  api: createCapabilityApi<SdiConnection>({
    capability: "sdi_invoicing",
    defaultVendor: "sdi_invoicing",
  }),
  connectionKeyField: "id" as keyof SdiConnection,
  getDisplayName: (c) => c.company_name ?? c.id ?? i18nSingleton._(msg`Azienda`),
  getSecondaryId: (c) => c.vat_number ?? null,
};
